import getNewRelicInstance from './getNewRelicInstance';
const SUPPRESSED_ERRORS = [
    'Initiator ([Video 18]) is not a Screen or a ScreenObject. Analytics would be confused',
    'Intl.PluralRules is not available in this environment. Try polyfilling it using "@formatjs/intl-pluralrules',
    'message: user have access restrictions',
    'r is undefined',
    'Script error',
    "'Document': 'div:has(> iframe[id='198230182308109283091823098102938908128390'])'",
    "Can't find variable: _33AcrossIdMappingsProvider",
    "Can't find variable: tlAdhesion",
    "Cannot read properties of null (reading 'removeChild')",
    "Cannot read properties of null (reading 'vdata",
    "Cannot read properties of undefined (reading 'image')",
    "null is not an object (evaluating 'e.parentNode.removeChild')",
    'ResizeObserver loop completed with undelivered notifications.',
    "Unexpected identifier 'Promise'",
    "Unexpected token '<'",
    'UET is not defined',
    // In release v1220 (Oct 7, 2022), NewRelic added handlers for unhandledrejection and unhandledPromiseRejection.
    // When we upgraded in June 2024, the new error reporting caused a storm. Suppress errors to simulate previous behavior.
    'Unhandled Promise Rejection',
    'Uncaught TypeError',
    'Uncaught RangeError',
    'Uncaught SyntaxError',
    'Uncaught ReferenceError: tlAdhesion is not defined',
    "TypeError: null is not an object (evaluating '(this.xdom?t:t.parent).document')",
    "TypeError: null is not an object (evaluating 'window.top.document')",
    'NS_ERROR_NOT_INITIALIZED:',
    "TypeError: null is not an object (evaluating 'this.topWindow",
    'Uncaught SecurityError: An attempt was made to break through the security policy of the user agent.',
    'Uncaught ReferenceError: setTimeout is not defined',
    "ReferenceError: Can't find variable: setTimeout",
    'TypeError: window.setTimeout is not a function.',
    "Uncaught SecurityError: Failed to read a named property 'setTimeout' from 'Window': Blocked a restricted frame with origin 'https://www.chegg.com' from accessing another frame.",
    'TypeError: window.top is null',
    'Uncaught SecurityError: Blocked a restricted frame with origin "https://www.chegg.com" from accessing another frame.',
    "Uncaught SecurityError: Failed to read a named property 'host' from 'Location': Blocked a restricted frame with origin 'https://www.chegg.com' from accessing another frame.",
    "Uncaught SecurityError: Failed to read a named property 'performance' from 'Window': Blocked a restricted frame with origin 'https://www.chegg.com' from accessing another frame.",
    "TypeError: undefined is not an object (evaluating 'this.adapter.resizeAd')",
    "Can't find variable: gmo",
    "TypeError: null is not an object (evaluating 'd.ownerDocument.defaultView.frameElement')"
];
// This is the code used by one-graph authorization layer. To rely on standard
// once GraphQL Error Standard is created.
// https://gitlab.com/chegginc/chegg-templates/typescript/libraries/node-framework/-/blob/main/packages/schema-authz/src/types/index.ts#L9
const AUTHORIZATION_ERROR_CODE = 'FORBIDDEN';
const SUPPRESSED_STACK_TRACE = [
    'chrome-extension://',
    'https://cdn.cookielaw.org/opt-out/otCCPAiab.js',
    'https://cdn.gladly.com/',
    'https://cdn.sprig.com',
    'injectCoinbaseWalletDappProvider.js',
    'safari-extension://',
    'window.gladlyWidgetLoader',
    'from accessing another frame'
];
// Check if its an error we want to suppress
const isSuppressedMessage = (errorMessage)=>process.env.CHEGG_ENV === 'prod' && // Compare the error string against the list of expected errors
    SUPPRESSED_ERRORS.some((e)=>errorMessage.toLowerCase().includes(e.toLowerCase()));
// Check if its an error we want to suppress
const isSuppressedStackTrace = (errorStackTrace)=>// Compare the error stack trace against the list of expected errors that contain that stacktrace
    SUPPRESSED_STACK_TRACE.some((e)=>errorStackTrace.toLowerCase().includes(e.toLowerCase()));
const isSuppressedCode = (errorCode)=>errorCode === AUTHORIZATION_ERROR_CODE;
/**
 * We don't know what types "error" from setErrorHandler actually can be, so, cover a number of bases here
 */ const canonicalizeError = (error)=>{
    if (typeof error === 'string') return {
        message: error.trim(),
        code: '',
        stack: ''
    };
    // If our `error` has any of the expected properties assume we have the neccessary information to proceed
    // covers cases where `error instanceof Error` and `error.constructor.name === 'UnhandledException'` among other potentially unknown cases
    if ('message' in error || 'extensions' in error || 'stack' in error) return {
        message: `${error.message || ''}`.trim(),
        // some utilities we make use of extend the Error object with code
        code: `${error.extensions?.code || ''}`.trim(),
        stack: `${error.stack || ''}`.trim()
    };
    // we set everything to an empty string and filter it out later
    return {
        message: '',
        code: '',
        stack: ''
    };
};
const valueIsFalsey = (value)=>!value;
const isCanonicalErrorEmpty = (error)=>Object.values(error).every(valueIsFalsey);
const isOddlyStructured = (error)=>{
    if (error.code && !error.message) return {
        group: `CheggWeb(UnknownError): Empty error 'message' detected with 'code' present.`
    };
    if (error.stack && !error.message) return {
        group: `CheggWeb(UnknownError): Empty error 'message' detected with 'stack' present.`
    };
    return false;
};
// method typing based on https://docs.newrelic.com/docs/browser/new-relic-browser/browser-apis/seterrorhandler/#examples
export const errorHandler = (error)=>{
    // if we somehow have a falsey error (unclear if this is possible in real world conditions) ignore it
    if (!error) return true;
    const canonicalizedError = canonicalizeError(error);
    return(// suppress errors which do not have useful error messages at all
    isCanonicalErrorEmpty(canonicalizedError) || // suppress errors whose code matches against list of expected client error code
    isSuppressedCode(canonicalizedError.code) || // suppress errors whose message matches against list of expected errors
    isSuppressedMessage(canonicalizedError.message) || // suppress errors whose stack matches against expected stack traces
    isSuppressedStackTrace(canonicalizedError.stack) || // group cases where the canonical error has an odd looking structure
    isOddlyStructured(canonicalizedError) || // let New Relic capture the error
    false);
};
// Called from app.tsx, will check if we should ignore the error
const setNewRelicErrorHandler = ()=>{
    // Run on the client only
    if (typeof window !== 'undefined' && Boolean(window.newrelic)) {
        const nrInstance = getNewRelicInstance();
        /**
     * On `as newrelic.ErrorHandler`
     *
     * the types for newrelic.ErrorHandler are wrong, even for the latest version of
     *  its types package, so, we force setErrorHandler to accept our errorHandler via type coersion
     */ nrInstance.setErrorHandler(errorHandler);
    }
};
export default setNewRelicErrorHandler;
